import { useDisclosure } from '@mantine/hooks'
import React, { Fragment, useRef } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useSelector, useDispatch } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { Link, useHistory } from 'react-router-dom'

import '../../css/breadCrumbs.css'
import { api_gateway } from '../../conexoes/api'
import { desabilitarAcoesCliente } from '../../redux/actions'
import { ESimsService } from '../../services/http/eSims.service'
import { useGlobal } from '../../stores/useGlobal'
import {
  dividerVertical,
  divisaDireita,
  setaVoltar
} from '../../utils/alternanciaIcones'
import { validadorPermissaoElemento } from '../../utils/verificadorPermissaoElemento'
import Icone from '../Icone'
import Botao from '../Mantine/Botao'
import Modal from '../Mantine/Modal'
import QuickButtons from '../QuickButtons'

import { getItemBreadCrumbDados, getTituloPagina } from './tituloPaginas'

const AVAILABLE_ATALHOS = ['/veye/esim/dispositivos', '/veye/esim/estoque']

const ROTA_MANEJADOR = {
  '/veye/esim/dispositivos': 'eSimDevices',
  '/veye/esim/estoque': 'eSimStock'
}

function BotaoVoltar() {
  const history = useHistory()

  return (
    <div onClick={() => history.goBack()} id="breadcrumbs-div-voltar">
      <img id="breadcrumbs-seta-voltar" src={setaVoltar()} alt="voltar" />
      <img
        id="breadcrumbs-divider-vertical"
        src={dividerVertical()}
        alt="divisor"
      />
    </div>
  )
}

const SetaDireita = () => (
  <img
    className="breadcrumbs-seta-direita"
    src={divisaDireita()}
    alt="seta direita"
  />
)

function BreadCrumbs() {
  const history = useHistory()
  const rota = history.location.pathname
  const { fetchFavActions, saveFavActions } = ESimsService
  const [opened, { open, close }] = useDisclosure(false)
  const despacho = useDispatch()
  const {
    setMostrarNotificacao,
    temItensTabelaSelecionados,
    setAcaoParaExecutar
  } = useGlobal()
  const quickButtonsRef = useRef(null)
  const queryClient = useQueryClient()
  const manejador = useSelector((state) => state.manejadorState.manejador)
  const permissaoElemento = useSelector(
    (state) => state.permissaoState.permissaoElemento
  )

  const isMaiorQue1255 = useMediaQuery({ minWidth: 1255 })
  const isMenorQue768 = useMediaQuery({ maxWidth: 767 })

  const breadCrumbs = useSelector(
    (state) => state.breadCrumbsState.breadCrumbsObj
  )

  const fetchAcoes = async (manejador) => {
    const response = await api_gateway.get(
      `/m1/consultar/acao?pa.n0_relevancia=1&pat.ds_acao=${manejador}`
    )
    const dados = response?.data?.dados || []
    const acoesComPermissao = await Promise.all(
      dados.map(async (acao) => {
        if (!acao?.ds_rota_permissao) return acao

        const temPermissao = await validadorPermissaoElemento(
          permissaoElemento || [],
          acao.ds_rota_permissao
        )

        return temPermissao ? acao : null
      })
    )

    return {
      ...response.data,
      dados: acoesComPermissao.filter(Boolean)
    }
  }

  const fetchFavoritas = async (manejadorParaFavoritas) => {
    const manejadorUsado = manejadorParaFavoritas ?? manejador
    const response = await fetchFavActions(manejadorUsado)
    if (Array.isArray(response)) return { dados: response }
    if (response?.dados != null) return response
    return { dados: [] }
  }

  const salvarFavoritas = async (acao, favoritas) => {
    await saveFavActions(
      acao,
      favoritas.map((fav, index) => ({
        cd_pav_acao: Number(fav.cd_pav_acao ?? fav.id ?? 0),
        order: fav.order ?? index
      }))
    )
  }

  const isRotaValida = AVAILABLE_ATALHOS.includes(rota)
  const manejadorAux = isRotaValida ? ROTA_MANEJADOR[rota] : manejador
  const getAcao = (acao) => acao.cd_pav_acao || acao.id || acao.ds_acao_redux

  //! ver se funciona rodar as querys somente depois que permissaoElemento nao for []
  const queryAcoesEFavoritas = useQuery(
    ['acoes-e-favoritas', manejadorAux, permissaoElemento],
    async () => {
      const [acoesResponse, favoritasResponse] = await Promise.all([
        fetchAcoes(manejadorAux),
        fetchFavoritas(manejadorAux)
      ])

      const acoes = acoesResponse?.dados || []
      const favoritas = favoritasResponse?.dados || []
      const acoesPermitidas = new Set(acoes.map((acao) => getAcao(acao)))
      const acoesFavoritasPermitidas = favoritas.filter((item) =>
        acoesPermitidas.has(getAcao(item))
      )
      const acoesFiltradas = acoes.filter((acao) => acao.fl_relatorio === 0)

      const idsFavoritas = acoesFavoritasPermitidas.map((item) =>
        getAcao(item)
      )
      const acoesSemFavoritas = acoesFiltradas.filter(
        (acao) => !idsFavoritas.includes(getAcao(acao))
      )

      return {
        acoes: acoesSemFavoritas,
        favoritas: acoesFavoritasPermitidas
      }
    },
    {
      enabled: isRotaValida && !!manejadorAux,
      staleTime: 0,
      cacheTime: 0,
      onError: () => {
        despacho(desabilitarAcoesCliente(false))
        setMostrarNotificacao({
          show: true,
          type: 'error',
          title: 'Erro ao carregar dados',
          message: 'Não foi possível carregar as ações e favoritas.'
        })
      },
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      retry: false
    }
  )

  if (rota === '/veye') return null

  if (!breadCrumbs || breadCrumbs?.length === 0) return null

  const breadCrumbArr = getItemBreadCrumbDados(breadCrumbs, rota)
  const tituloPagina = getTituloPagina(breadCrumbArr)

  const executarAcao = (acaoRedux) => {
    if (!acaoRedux) return

    setAcaoParaExecutar(acaoRedux)
  }

  const handleConfirm = async () => {
    if (!quickButtonsRef.current) {
      close()
      return
    }
    const resultado = await quickButtonsRef.current.salvar()
    if (!resultado || !resultado.sucesso) {
      close()
      return
    }
    try {
      await salvarFavoritas(manejadorAux, resultado.favoritasAtualizadas)
      const dadosAtuais = queryAcoesEFavoritas.data
      const favoritasComOrder = resultado.favoritasAtualizadas.map(
        (fav, index) => ({
          ...fav,
          order: index
        })
      )
      if (dadosAtuais) {
        queryClient.setQueryData(
          ['acoes-e-favoritas', manejadorAux],
          {
            ...dadosAtuais,
            favoritas: favoritasComOrder,
            acao: manejadorAux
          }
        )
      }
      await queryClient.invalidateQueries([
        'acoes-e-favoritas',
        manejadorAux])
      close()
    } catch (error) {
      setMostrarNotificacao({
        show: true,
        type: 'error',
        title: 'Erro ao salvar favoritos',
        message: 'Não foi possível salvar as ações favoritas.'
      })
    }
  }

  const favoritas = queryAcoesEFavoritas?.data?.favoritas || []
  const temFavoritos = favoritas.length > 0
  const gridTemplateColumns =
    !temFavoritos && isMenorQue768
      ? '1fr'
      : isMaiorQue1255 || !temFavoritos
        ? '1fr auto'
        : '1fr'

  return (
    <nav
      aria-label="Breadcumb"
      className="container-bread-crumbs"
      style={{ gridTemplateColumns }}
    >
      <div className="breadcrumbs-inner-container">
        <BotaoVoltar />
        <div id="div-bread-crumbs">
          <h4 id="titulo-pagina">{tituloPagina}</h4>
          <div>
            <Link to="/veye" className="fonte-cor-18">
              Home
            </Link>

            {breadCrumbArr.map(({ rota, label }) => {
              const text = label === 'mensagens' ? tituloPagina : label

              return (
                <Fragment key={`${rota}=${label}`}>
                  <SetaDireita />
                  {rota ? (
                    <Link to={rota} className="span-bread-crumbs fonte-cor-18">
                      {text}
                    </Link>
                  ) : (
                    <span className="span-bread-crumbs fonte-cor-18">
                      {text}
                    </span>
                  )}
                </Fragment>
              )
            })}
          </div>
        </div>
      </div>
      {AVAILABLE_ATALHOS.includes(rota) && (
        <div className="breadcrumbs-botao-container">
          {(queryAcoesEFavoritas?.data?.favoritas || [])
            .slice()
            .sort(
              (a, b) =>
                (a.order ?? a.ds_ordem ?? 999) - (b.order ?? b.ds_ordem ?? 999)
            )
            .map((favorita) => ({
              id: favorita.cd_pav_acao,
              name: favorita.ds_acao,
              ds_acao_redux: favorita.ds_acao_redux,
              fl_selecionado: favorita.fl_selecionado
            }))
            .map((button) => {
              const deveDesabilitar =
                button.fl_selecionado === 0 && !temItensTabelaSelecionados

              return (
                <Botao
                  key={button.id}
                  label={button.name}
                  variant="primary"
                  uppercase={false}
                  disabled={deveDesabilitar}
                  onClick={() => {
                    executarAcao(button.ds_acao_redux)
                  }}
                />
              )
            })}
          <div className="breadcrumbs-personalizar-atalhos">
            <Botao
              label="Personalizar atalhos"
              variant="outline"
              uppercase={false}
              width="100%"
              onClick={() => open()}
              rightIcon={
                <Icone nome="edit" color="primary" width="20px" height="20px" />
              }
            />
          </div>
        </div>
      )}
      {opened && (
        <Modal
          width="670px"
          type="default"
          titulo="Personalizar atalhos"
          opened={opened}
          close={close}
          onConfirm={handleConfirm}
        >
          <QuickButtons
            ref={quickButtonsRef}
            acoesData={queryAcoesEFavoritas?.data?.acoes || []}
            favoritasData={queryAcoesEFavoritas?.data?.favoritas || []}
            manejador={manejadorAux}
            isOpen={opened}
          />
        </Modal>
      )}
    </nav>
  )
}

export default BreadCrumbs
